import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { Button, DatePicker, Divider, Form, Menu, Radio, Row } from "antd";
import moment from "moment";

import Swal from "sweetalert2";

import memberApi from "../../../apis/MemberManage.api";
import statisticsApi from "../../../apis/Statistics.api";
import { setLoading } from "../../../redux/store/common/commonDuck";
import CustomLoading from "../../custom/CustomLoading";
import ContentHeaderComponent from "../../layout/ContentHeaderComponent";

import StatisticsGraph from "./StatisticsGraph";

const dateTypeItems = [
  { label: "1주일", value: "1week" },
  { label: "1개월", value: "1month" },
  { label: "3개월", value: "3month" },
];

const InflowComponent = () => {
  const dispatch = useDispatch();

  const user = useSelector((state) => state.auth.user);
  const isLoading = useSelector((state) => state.common.loading);
  const setIsLoading = (value) => dispatch(setLoading(value));

  const [navType, setNavType] = useState("mall");
  const [optionList, setOptionList] = useState([]);

  const [dateType, setDateType] = useState("1week");
  const [searchOptions, setSearchOptions] = useState({
    searchType: "order_created_at",
    searchStatus: "gift_address_ready",
    searchDetailType: "",
    searchDetail: "",
    searchOrderId: "",
    funnel: 1, // 유입경로, 1:전체
    name: "",
    phone: "",
    startDate: moment().subtract(7, "days").startOf("day"),
    endDate: moment().endOf("day"),
  });

  const [summary, setSummary] = useState({});
  const [graphData, setGraphData] = useState({});

  /**
   * 1주일, 1개월, 3개월
   * @param e
   */
  const handleDateTypeChange = (e) => {
    const value = e.target.value;
    setDateType(value);
    switch (value) {
      case "1week":
        if (searchOptions.endDate === null || searchOptions.endDate === "") {
          setSearchOptions({
            ...searchOptions,
            startDate: moment().subtract(7, "days").startOf("day"),
            endDate: moment().endOf("day"),
          });
        } else {
          setSearchOptions({
            ...searchOptions,
            startDate: moment(searchOptions.endDate.format("YYYY/MM/DD"))
              .subtract(7, "days")
              .startOf("day"),
          });
        }
        break;
      case "1month":
        if (searchOptions.endDate === null || searchOptions.endDate === "") {
          setSearchOptions({
            ...searchOptions,
            startDate: moment().subtract(1, "months").startOf("day"),
            endDate: moment().endOf("day"),
          });
        } else {
          setSearchOptions({
            ...searchOptions,
            startDate: moment(searchOptions.endDate.format("YYYY/MM/DD"))
              .subtract(1, "months")
              .startOf("day"),
          });
        }
        break;
      case "3month":
        if (searchOptions.endDate === null || searchOptions.endDate === "") {
          setSearchOptions({
            ...searchOptions,
            startDate: moment().subtract(3, "months").startOf("day"),
            endDate: moment().endOf("day"),
          });
        } else {
          setSearchOptions({
            ...searchOptions,
            startDate: moment(searchOptions.endDate.format("YYYY/MM/DD"))
              .subtract(3, "months")
              .startOf("day"),
          });
        }
        break;
    }
  };

  /**
   * 초기화
   */
  const handleInitial = () => {
    setSearchOptions({
      searchType: "order_created_at",
      searchStatus: "gift_address_ready",
      searchOrderId: "",
      funnel: 1,
      name: "",
      phone: "",
      startDate: moment().subtract(7, "days").startOf("day"),
      endDate: moment().endOf("day"),
    });
    setDateType("1week");
    // setPageSize(10);
  };

  /**
   * 최대 30일 조회 기간 설정
   * @param value
   * @param type
   */
  const handleDatePicker = (value, type) => {
    setDateType("");
    if (type === "startDate") {
      let diffDays = moment(searchOptions.endDate).diff(value, "days");
      if (diffDays > 0) {
        if (diffDays >= 30) {
          // alert("조회 기간은 30일 이내로 설정 가능합니다.");
          Swal.fire({
            title: "조회 기간은 30일 이내로 설정 가능합니다.",
            customClass: "custom-alert",
            confirmButtonColor: "#117ffa",
            confirmButtonText: "확인",
          });
          handleInitial();
        } else {
          setSearchOptions({ ...searchOptions, [type]: value });
        }
      } else {
        Swal.fire({
          title: "시작 날짜와 마지막 날짜를 정확히 선택해주세요.",
          customClass: "custom-alert",
          confirmButtonColor: "#117ffa",
          confirmButtonText: "확인",
        });
        handleInitial();
      }
    } else {
      let diffDays = moment(value).diff(searchOptions.startDate, "days");
      if (diffDays > 0) {
        if (diffDays >= 30) {
          Swal.fire({
            title: "조회 기간은 30일 이내로 설정 가능합니다.",
            customClass: "custom-alert",
            confirmButtonColor: "#117ffa",
            confirmButtonText: "확인",
          });
          handleInitial();
        } else {
          setSearchOptions({ ...searchOptions, [type]: value });
        }
      } else {
        Swal.fire({
          title: "시작 날짜와 마지막 날짜를 정확히 선택해주세요.",
          customClass: "custom-alert",
          confirmButtonColor: "#117ffa",
          confirmButtonText: "확인",
        });
        handleInitial();
      }
    }
  };

  useEffect(() => {
    getInfluencerList();
  }, []);

  /**
   * 인플루언서 리스트
   * @returns {Promise<void>}
   */
  const getInfluencerList = async () => {
    const res = await memberApi.readAppExpertList();
    if (res.code === 200) {
      let list = res.result?.expertList;
      if (list?.length) {
        setOptionList([
          {
            value: 1,
            label: "전체",
          },
          ...list.map((item) => ({
            value: item.partners_id,
            label: item.name,
          })),
        ]);
      }
    }
  };

  useEffect(() => {
    getInflowDataGraph(); // user, dateType, searchOptions, navType
  }, [user, dateType, searchOptions, navType]);

  /**
   * 매출 그래프 데이터 조회
   * @returns {Promise<void>}
   */
  const getInflowDataGraph = useCallback(async () => {
    setIsLoading(true);
    // 당영몰: 0, 드럭스토어 정보 가져올때 인플루언서 전체: 1, 나머지는 인플루언서 id
    let partners_id = navType === "mall" ? 0 : searchOptions.funnel;

    const param = {
      searchDateType: dateType?.length > 0 ? dateType : "custom",
      startDate: moment(searchOptions.startDate).format("YYYYMMDDHHmmss"),
      endDate: moment(searchOptions.endDate).format("YYYYMMDDHHmmss"),
      partnersId: partners_id,
      userId: user.id === 724 ? 1 : user.id,
    };
    const res = await statisticsApi.readInflowDataGraph(param);
    if (res.code === 200) {
      setSummary(res.result.totalSummary);
      // total_sales_amount, total_purchases_num, unit_price_avg
      setGraphData(res.result.userStatistics);
    }
    setIsLoading(false);
  }, [user, dateType, searchOptions, navType]);

  return (
    <div style={{ position: "relative" }}>
      {isLoading && <CustomLoading />}
      <ContentHeaderComponent title="유입분석" />
      <Menu
        className={`commission-nav`}
        mode="horizontal"
        defaultSelectedKeys={["mall"]}
        selectedKeys={[navType]}
        style={{ gap: 0 }}
      >
        <Menu.Item key={`mall`} onClick={() => setNavType("mall")}>
          당영몰
        </Menu.Item>
        <Menu.Item key={`app`} onClick={() => setNavType("app")}>
          드럭스토어
        </Menu.Item>
      </Menu>
      <div className="product-register-wrap">
        <div className="product-search-box-wrap">
          <Form.Item
            label="조회기간"
            labelAlign="left"
            colon={false}
            labelCol={{ span: 3 }}
            wrapperCol={{ span: 21 }}
            style={{ marginBottom: 0 }}
          >
            <Row align={"middle"} style={{ gap: "5px", rowGap: "5px" }}>
              <Radio.Group
                className={`options-list`}
                options={dateTypeItems}
                onChange={handleDateTypeChange}
                value={dateType}
                optionType="button"
              />
              <DatePicker
                className="order-search-date-wrap"
                format="YYYY/MM/DD"
                value={searchOptions.startDate}
                onChange={(value) => handleDatePicker(value, "startDate")}
                allowClear={false}
              />
              ~
              <DatePicker
                className="order-search-date-wrap"
                format="YYYY/MM/DD"
                value={searchOptions.endDate}
                onChange={(value) => handleDatePicker(value, "endDate")}
                allowClear={false}
              />
            </Row>
          </Form.Item>
          {navType === "app" && (
            <>
              <Divider className="product-register-form-input-divider" />
              <Form.Item
                className="center-label"
                label="인플루언서"
                labelAlign="left"
                colon={false}
                labelCol={{ span: 3 }}
                wrapperCol={{ span: 21 }}
                style={{ marginBottom: 0 }}
              >
                <Radio.Group
                  options={optionList}
                  onChange={(e) =>
                    setSearchOptions({
                      ...searchOptions,
                      funnel: e.target.value,
                    })
                  }
                  value={searchOptions.funnel}
                />
              </Form.Item>
            </>
          )}
          {/*<Row className="product-search-button-wrap" justify="center">*/}
          {/*	<Button*/}
          {/*		className="product-search-button search"*/}
          {/*		onClick={handleSearch}*/}
          {/*	>*/}
          {/*		검색*/}
          {/*	</Button>*/}
          {/*	<Button*/}
          {/*		className="product-search-button initial"*/}
          {/*		onClick={handleInitialBtnClick}*/}
          {/*	>*/}
          {/*		초기화*/}
          {/*	</Button>*/}
          {/*</Row>*/}
        </div>
        <StatisticsGraph
          title={"유입분석"}
          searchOptions={searchOptions}
          summary={summary}
          graphData={graphData}
        />
      </div>
    </div>
  );
};
export default InflowComponent;
